import { emit } from "@tauri-apps/api/event";
import { prepareScreenRecordings } from "./screen-recording-attachments";
import { recordVoiceActivity } from "./voice-dispatch-activity";
import {
  acceptVoiceRecordings,
  prepareVoiceRecordings,
  withVoiceRecordingDraft,
} from "./voice-recording-draft";
import { captureDesktopChatAuthScopeGuard } from "./desktop-chat-auth-scope";
import {
  dispatchUserMessage,
  type DispatchUserMessageArgs,
} from "@/hooks/use-dispatch-user-message";
import { useCanvasStore } from "@/stores/canvas-store";
import { inputDestinationKey } from "@/lib/input-destination-key";
import { useInputBarTarget } from "@/stores/input-bar-target-store";
import { uploadImage } from "@/api/uploads";
import { recordSessionLatency } from "@/lib/session-latency-trace";
import { globalPointToScreenshotPoint } from "./screenshot-pointer";
import { useActiveDisplay } from "./use-active-display";
import { voiceScreenshotCaptureTarget } from "./cursor-input-screenshot";
import { getWindowOrigin } from "./use-window-origin";
import {
  activeAgentSupportsScreenshotAttachment,
  attachScreenshotIfRequested,
} from "./use-screenshot-attach";
import {
  buildVoiceAttentionContext,
  hasVoiceAttentionStrokes,
  type VoiceAttentionChapterAttachment,
  type VoiceAttentionState,
  useVoiceAttentionStore,
} from "./voice-attention-store";
import type { VoiceAttentionChapterCapture } from "./voice-attention-chapter-capture";
import {
  awaitMergedVoiceAttentionCaptures,
  voiceAttentionRecordingIds,
} from "./voice-attention-merge";
import { registerOutboundScreensForImages } from "./outbound-screenshot-metadata";
import { useScreenshotSettings } from "./screenshot-settings";
import {
  createImageFramesForUploads,
  type UploadedImage,
} from "./use-image-frame-creation";
import {
  answerAwaitingInputFromVoice,
  resolveAwaitingVoiceReplyTarget,
} from "./voice-awaiting-target";
import { requestsForegroundScreenContext } from "./foreground-screen-intent";
import { holdSendForGuide } from "./onboarding/quick-guide/guide-send-hold";
import {
  destinationDraftComposerId,
  restoreDestinationDraft,
} from "@/hooks/use-destination-draft";
import { isPromptRequestRetained } from "@/lib/prompt-receipt-bus";
import { retainPromptRetryEnvelope } from "@/lib/prompt-retry-envelope";
import { isSubagentTarget } from "@/lib/input-bar-target-kind";
import { nativeChildSubmitOptions } from "@/lib/input-bar-target-submit";
import { registerLiveTalkPromptRequest } from "./live-talk-speech-ownership";
import {
  beginCompactPromptScreenshotPreview,
  finishCompactResources,
} from "./compact-resource-controller";
import {
  activeConversationComposerFrameId,
  settlePendingVoiceNewChatIntent,
  traceVoiceNewChatRouting,
} from "./voice-new-chat-routing";
import {
  clearOptimisticVoiceMessage,
  rekeyOptimisticVoiceMessage,
  showOptimisticVoiceMessage,
} from "./optimistic-voice-message";

export type VoicePromptSource = "dictation" | "continuous";

export type DispatchVoicePromptArgs = {
  text: string;
  source: VoicePromptSource;
  screenshotIntent: boolean;
  startNewConversation?: boolean;
  clientRequestId?: string;
  isCancelled?: () => boolean;
  attentionState?: VoiceAttentionState;
  /** Sent after the agent's turn ended: its reply keeps playing. */
  queuesBehindReply?: boolean;
};

type VoiceDispatchArgs = DispatchUserMessageArgs & { composerId: string };

function captureHasMappableAttention(
  capture: VoiceAttentionChapterCapture,
  snapshot: VoiceAttentionState,
): boolean {
  if (!snapshot.chapters.some((chapter) => chapter.id === capture.chapterId)) {
    return false;
  }
  return snapshot.strokes.some((stroke) =>
    stroke.points.some(
      (point) =>
        point.chapterId === capture.chapterId &&
        globalPointToScreenshotPoint(capture.screen, point) !== null,
    ),
  );
}

export function dispatchVoicePrompt(
  args: DispatchVoicePromptArgs,
): Promise<boolean> {
  return withVoiceRecordingDraft(args, dispatchVoicePromptImpl);
}

async function dispatchVoicePromptImpl({
  text,
  source,
  screenshotIntent,
  startNewConversation = false,
  clientRequestId,
  isCancelled,
  attentionState: suppliedAttentionState,
  queuesBehindReply = false,
}: DispatchVoicePromptArgs): Promise<boolean> {
  const isCurrentAuth = captureDesktopChatAuthScopeGuard();
  const trimmed = text.trim();
  if (!trimmed || !isCurrentAuth() || isCancelled?.()) return false;
  if (holdSendForGuide("voice")) return false;
  const attentionState =
    suppliedAttentionState ?? useVoiceAttentionStore.getState();
  const recordingId = attentionState.recordingId;
  const recordingIds = voiceAttentionRecordingIds(attentionState);
  const inputBarRoutingIntent = useInputBarTarget
    .getState()
    .snapshotRoutingIntent();
  const voiceComposerId =
    source === "dictation"
      ? (inputBarRoutingIntent.newConversationFrameId ??
        activeConversationComposerFrameId())
      : null;
  const clearAcceptedVoiceComposer = () => {
    if (!voiceComposerId) return;
    // The composer showing the dictated draft may live in another desktop
    // window than the one running this dispatch (multi-monitor). Tauri's
    // `emit` reaches every window, this one included.
    void emit("denker:voice-prompt-accepted", {
      composerId: `conversation-frame:${voiceComposerId}`,
    }).catch(() => {});
  };
  // The bar's draft, by destination: a view switch neither aborts the
  // prompt nor moves where a rejected transcript goes back to.
  const barDestination = inputDestinationKey({
    startNewConversation: inputBarRoutingIntent.startNewConversation,
    conversationId: inputBarRoutingIntent.target?.conversationId,
  });
  const abandoned = () => !isCurrentAuth() || !!isCancelled?.();
  const restoreOriginalDraft = () => {
    if (isCurrentAuth()) restoreDestinationDraft(barDestination, trimmed);
  };

  const awaitingTarget = startNewConversation
    ? null
    : await resolveAwaitingVoiceReplyTarget();
  if (abandoned()) return false;
  if (awaitingTarget) {
    let sent = false;
    let acceptedClipIds: string[] = [];
    try {
      const recordings = await prepareScreenRecordings(
        recordingIds,
        0,
        abandoned,
        !awaitingTarget.awaitingInputId.startsWith("onboarding:") &&
          activeAgentSupportsScreenshotAttachment(),
      );
      acceptedClipIds = recordings.context?.clips.map((clip) => clip.id) ?? [];
      if (abandoned()) return false;
      sent = await answerAwaitingInputFromVoice(
        awaitingTarget,
        trimmed,
        recordings.images.flatMap((image) =>
          image.imageId ? [image.imageId] : [],
        ),
        abandoned,
        recordings.context,
      );
    } catch {
      sent = false;
    }
    if (sent) {
      clearAcceptedVoiceComposer();
      recordVoiceActivity(trimmed);
      acceptVoiceRecordings(attentionState, acceptedClipIds);
    } else restoreOriginalDraft();
    return sent;
  }

  // A pending New chat lends its intent id, so the accepted receipt moves
  // the bar to the chat it creates, also when it was started by typing.
  const voiceRequestId =
    clientRequestId ??
    (inputBarRoutingIntent.startNewConversation
      ? inputBarRoutingIntent.intentId
      : null) ??
    crypto.randomUUID();
  recordSessionLatency("voice.submitted", {
    clientRequestId: voiceRequestId,
    recordingId,
  });
  showOptimisticVoiceMessage({
    requestId: voiceRequestId,
    text: trimmed,
    conversationId: inputBarRoutingIntent.target?.conversationId,
    sessionId: inputBarRoutingIntent.target?.runtimeSessionId,
    conversationFrameId: inputBarRoutingIntent.newConversationFrameId,
  });
  // Unfinished or abandoned recordings retract the optimistic message and,
  // unless cancelled, return the transcript to the draft for a retry.
  const prepareRecordings = (attachmentCount: number) =>
    prepareVoiceRecordings(
      recordingIds,
      attachmentCount,
      activeAgentSupportsScreenshotAttachment(),
      abandoned,
      () => {
        clearOptimisticVoiceMessage(voiceRequestId);
        if (!isCancelled?.()) restoreOriginalDraft();
      },
    );
  const liveCursor = useActiveDisplay.getState();
  const activation = attentionState.activation;
  const activeDisplayId = activation?.display_id ?? liveCursor.activeDisplayId;
  const cursorX = activation?.x ?? liveCursor.cursorX;
  const cursorY = activation?.y ?? liveCursor.cursorY;
  const origin = getWindowOrigin();
  const localX = cursorX - origin.x;
  const localY = cursorY - origin.y;
  const screenToFlow = useCanvasStore.getState().screenToFlowPosition;
  const position = screenToFlow
    ? screenToFlow({ x: localX, y: localY })
    : { x: 0, y: 0 };
  let imageIds: string[] = [];
  let pointerToken: string | null = null;
  let displaysToken: string | null = null;
  let screens: Awaited<
    ReturnType<typeof attachScreenshotIfRequested>
  >["screens"] = [];
  let previews: Awaited<
    ReturnType<typeof attachScreenshotIfRequested>
  >["previews"] = [];
  // Hands a prepared prompt to dispatch and settles the voice UI around it.
  const sendVoiceDispatch = async (
    dispatchArgs: VoiceDispatchArgs,
    sentPreviews: typeof previews = [],
  ): Promise<boolean> => {
    if (!isCurrentAuth()) return false;
    let result: ReturnType<typeof dispatchUserMessage>;
    try {
      result = dispatchUserMessage(dispatchArgs);
    } catch (error) {
      clearOptimisticVoiceMessage(voiceRequestId);
      throw error;
    }
    const resolvedVoiceRequestId = result.clientRequestId ?? voiceRequestId;
    if (result.sent && resolvedVoiceRequestId !== voiceRequestId) {
      rekeyOptimisticVoiceMessage(voiceRequestId, resolvedVoiceRequestId);
    }
    // A native child's receipts carry no prompt_source, so its reply is never
    // adopted as a live-talk turn; registering it would only leave an entry.
    if (
      source === "continuous" &&
      !dispatchArgs.nativeChildInstruction &&
      result.sent &&
      result.clientRequestId
    ) {
      registerLiveTalkPromptRequest(result.clientRequestId, queuesBehindReply);
    }
    if (!result.sent) {
      clearOptimisticVoiceMessage(voiceRequestId);
      if (result.skippedReason === "local_command") {
        return false;
      }
      restoreOriginalDraft();
      throw new Error("Voice prompt was not sent to an agent session.");
    }
    const previewRequestId = result.clientRequestId;
    if (previewRequestId && sentPreviews.length > 0) {
      beginCompactPromptScreenshotPreview(previewRequestId, sentPreviews);
    }
    const accepted = await result.accepted;
    if (!isCurrentAuth()) return false;
    if (!accepted) {
      clearOptimisticVoiceMessage(resolvedVoiceRequestId);
      if (previewRequestId) finishCompactResources(previewRequestId);
      retainPromptRetryEnvelope({
        composerId: dispatchArgs.composerId,
        draftText: trimmed,
        args: result.retryEnvelopeArgs ?? dispatchArgs,
        clientRequestId:
          result.clientRequestId &&
          isPromptRequestRetained(result.clientRequestId)
            ? result.clientRequestId
            : undefined,
      });
      restoreOriginalDraft();
      return false;
    }

    clearAcceptedVoiceComposer();
    recordVoiceActivity(trimmed);
    acceptVoiceRecordings(
      attentionState,
      dispatchArgs.screenRecordings?.clips.map((clip) => clip.id) ?? [],
    );
    const settledNewChatIntentId = dispatchArgs.startNewConversation
      ? (clientRequestId ?? inputBarRoutingIntent.intentId)
      : null;
    if (settledNewChatIntentId) {
      settlePendingVoiceNewChatIntent(settledNewChatIntentId);
    }
    return true;
  };

  const subagentTarget = inputBarRoutingIntent.target;
  if (!startNewConversation && isSubagentTarget(subagentTarget)) {
    // A child receives literal speech plus explicitly recorded samples.
    // Voice carries imageIds directly, so allowImages is a cursor-bar option.
    const {
      textOnly,
      allowImages: _allowVoiceImages,
      ...nativeOptions
    } = nativeChildSubmitOptions(subagentTarget.conversationId);
    const recordings = await prepareRecordings(0);
    if (!recordings) return false;
    return sendVoiceDispatch({
      ...nativeOptions,
      ...(recordings.context
        ? {
            screenRecordings: recordings.context,
            imageIds: recordings.images.flatMap((image) =>
              image.imageId ? [image.imageId] : [],
            ),
          }
        : {}),
      literalText: textOnly,
      text: trimmed,
      position,
      promptSource: source,
      originDisplayId: activeDisplayId,
      clientRequestId: voiceRequestId,
    });
  }
  const chapterCapturePromise =
    hasVoiceAttentionStrokes(attentionState) &&
    activeAgentSupportsScreenshotAttachment()
      ? awaitMergedVoiceAttentionCaptures(attentionState, 2_500)
      : Promise.resolve([]);
  const chapterUploadPromise = chapterCapturePromise.then(async (captures) => {
    if (abandoned()) {
      return { chapterCaptures: [], chapterUploadResults: [] };
    }
    const chapterCaptures = captures.filter((capture) =>
      captureHasMappableAttention(capture, attentionState),
    );
    const chapterUploadResults = await Promise.allSettled(
      chapterCaptures.map((capture) => uploadImage(capture.file)),
    );
    return { chapterCaptures, chapterUploadResults };
  });
  try {
    const foregroundCapture = requestsForegroundScreenContext(trimmed);
    const r = await attachScreenshotIfRequested(
      screenshotIntent || foregroundCapture,
      { x: cursorX, y: cursorY },
      voiceScreenshotCaptureTarget(activeDisplayId),
    );
    imageIds = [...r.imageIds];
    pointerToken = r.pointerToken;
    displaysToken = r.displaysToken;
    screens = [...r.screens];
    previews = [...(r.previews ?? [])];
  } catch (e) {
    console.warn("[voice] screenshot capture failed:", e);
  }
  if (abandoned()) {
    clearOptimisticVoiceMessage(voiceRequestId);
    return false;
  }

  const { chapterCaptures, chapterUploadResults } = await chapterUploadPromise;
  if (abandoned()) {
    clearOptimisticVoiceMessage(voiceRequestId);
    return false;
  }
  const chapterAttachments: VoiceAttentionChapterAttachment[] = [];
  const chapterUploads: UploadedImage[] = [];
  const chapterPreviews: typeof previews = [];
  for (let index = 0; index < chapterUploadResults.length; index += 1) {
    const result = chapterUploadResults[index]!;
    const capture = chapterCaptures[index]!;
    if (result.status === "rejected") {
      console.warn("[voice] attention chapter upload failed:", result.reason);
      continue;
    }
    const screen = {
      ...capture.screen,
      index: screens.length,
    };
    imageIds.push(result.value.id);
    screens.push(screen);
    chapterAttachments.push({
      chapterId: capture.chapterId,
      imageId: result.value.id,
      screen,
    });
    chapterUploads.push({
      id: result.value.id,
      url: result.value.url,
      width: result.value.width,
      height: result.value.height,
      filename: result.value.original_filename || undefined,
    });
    if (capture.preview) chapterPreviews.push(capture.preview);
  }
  previews.push(...chapterPreviews);
  if (chapterAttachments.length > 0) {
    registerOutboundScreensForImages(imageIds, screens);
    if (useScreenshotSettings.getState().copySentImagesToCanvas) {
      try {
        await createImageFramesForUploads(chapterUploads);
      } catch (error) {
        clearOptimisticVoiceMessage(voiceRequestId);
        throw error;
      }
    }
  }
  if (abandoned()) {
    clearOptimisticVoiceMessage(voiceRequestId);
    return false;
  }

  const recordings = await prepareRecordings(imageIds.length);
  if (!recordings) return false;
  imageIds.push(
    ...recordings.images.flatMap((image) =>
      image.imageId ? [image.imageId] : [],
    ),
  );

  // Only the prefixes are trimmed away; an untokenised prompt is passed through
  // exactly as before, including any whitespace the caller intended.
  const prefixes = [displaysToken, pointerToken].filter(Boolean).join(" ");
  const finalText = prefixes ? `${prefixes} ${trimmed}`.trim() : trimmed;
  const voiceAttention = buildVoiceAttentionContext(
    chapterAttachments,
    attentionState,
  );

  const composerId = destinationDraftComposerId(barDestination);
  const usesCanonicalNewChatIntent =
    startNewConversation &&
    inputBarRoutingIntent.startNewConversation &&
    !!inputBarRoutingIntent.intentId &&
    (!clientRequestId || clientRequestId === inputBarRoutingIntent.intentId);
  const dispatchArgs: VoiceDispatchArgs = {
    text: finalText,
    position,
    imageIds,
    promptSource: source,
    originDisplayId: activeDisplayId,
    startNewConversation:
      startNewConversation || inputBarRoutingIntent.startNewConversation,
    inputBarRoutingIntent:
      !startNewConversation || usesCanonicalNewChatIntent
        ? inputBarRoutingIntent
        : undefined,
    conversationFrameId: usesCanonicalNewChatIntent
      ? (inputBarRoutingIntent.newConversationFrameId ?? undefined)
      : undefined,
    ignoreInputBarSessionState:
      startNewConversation && !usesCanonicalNewChatIntent,
    composerId,
    clientRequestId: voiceRequestId,
    voiceAttention,
    screenRecordings: recordings.context,
  };
  traceVoiceNewChatRouting("outbound_dispatch", {
    source,
    explicitStartNewConversation: startNewConversation,
    sharedStartNewConversation: inputBarRoutingIntent.startNewConversation,
    outboundStartNewConversation: dispatchArgs.startNewConversation,
    usesCanonicalNewChatIntent,
    clientRequestId: voiceRequestId,
    sharedIntentId: inputBarRoutingIntent.intentId,
    conversationFrameId: dispatchArgs.conversationFrameId ?? null,
  });
  return sendVoiceDispatch(dispatchArgs, previews);
}
